import { AsyncPipe } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	Validators,
} from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { filter, takeUntil, withLatestFrom } from "rxjs/operators";

import {
	IntegrationsDTO,
	LoginPayloadParameters,
	LoginUserDTO,
	OrganizationSettingsDTO,
	ValidationSettingsDTO,
} from "@guest-web/api";
import { TrpButtonComponent } from "@triparound/trp-components/button";
import {
	TrpDialogDirective,
	TrpToastOptions,
	TrpToastUtil,
} from "@triparound/trp-components/feedback";
import {
	TrpCheckboxComponent,
	TrpInputComponent,
	TrpPasswordInputComponent,
} from "@triparound/trp-components/inputs";
import { TrpImageComponent, TrpPoweredComponent } from "@triparound/trp-components/media";
import { ignoreNil, TrpNotificationType, TrpPosition } from "@triparound/trp-components/shared";

import { CommonFacadeService } from "../@core/services/common-facade.service";
import { CompanyFacadeService } from "../@core/services/company-facade.service";
import { NavigationFacadeService } from "../@core/services/navigation-facade.service";
import { SafePipe } from "../@shared/common/pipes/safe/safe.pipe";

import { AuthenticationFacadeService } from "./authentication-facade.service";

@Component({
	selector: "app-login",
	templateUrl: "./login.component.html",
	styleUrls: ["./login.component.scss"],
	imports: [
		TrpImageComponent,
		FormsModule,
		ReactiveFormsModule,
		TrpPasswordInputComponent,
		TrpInputComponent,
		TrpCheckboxComponent,
		TrpButtonComponent,
		TrpPoweredComponent,
		TrpDialogDirective,
		AsyncPipe,
		SafePipe,
	],
})
export class LoginComponent implements OnInit, OnDestroy {
	@ViewChild("termsDialog") termsDialog!: TrpDialogDirective;
	@ViewChild("privacyDialog") privacyDialog!: TrpDialogDirective;

	loginForm: FormGroup = new FormGroup({});
	get rememberMe(): FormControl {
		return this.loginForm.get("rememberMe") as FormControl;
	}

	guestValidationSettings$: Observable<ValidationSettingsDTO>;
	guestValidationIntegrations$: Observable<IntegrationsDTO>;
	loginFormControls$: BehaviorSubject<
		{ label: string; field: string; tooltip: string; placeholder: string }[]
	> = new BehaviorSubject([]);
	formHasRoomAndReservation$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	hasIntegration$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	hasVerityIntegration$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	loginSubmit$: BehaviorSubject<boolean> = new BehaviorSubject(null);
	organizationInfo$: Observable<OrganizationSettingsDTO>;
	businessLogo: string;
	terms: string;
	privacy: string;
	loginUser$: Observable<LoginUserDTO>;
	returnUrl: string;
	RESERVATION_TOOLTIP_TXT =
		"Reservation ID also referred to as Trip ID - is the confirmation number received in the email sent to the address provided during booking.";

	destroy$: Subject<boolean> = new Subject<boolean>();

	constructor(
		private route: ActivatedRoute,
		private companyFacade: CompanyFacadeService,
		private authenticationFacade: AuthenticationFacadeService,
		private navigationFacade: NavigationFacadeService,
		private commonFacade: CommonFacadeService,
		private toastUtil: TrpToastUtil,
	) {
		this.businessLogo = this.companyFacade.organizationInfo?.logo;
		this.terms = this.companyFacade.organizationInfo?.policy;
		this.privacy = this.companyFacade.organizationInfo?.privacy;
		this.guestValidationSettings$ = this.authenticationFacade.guestValidationSettings$;
		this.guestValidationIntegrations$ = this.authenticationFacade.guestValidationIntegrations$;
		this.organizationInfo$ = this.companyFacade.organizationInfo$;
		this.loginUser$ = this.authenticationFacade.loginUser$;
		this.returnUrl = this.route.snapshot.queryParams["returnUrl"] || "";
	}

	ngOnInit(): void {
		this.authenticationFacade.clearLoginState();

		combineLatest([this.guestValidationIntegrations$, this.guestValidationSettings$])
			.pipe(
				filter(
					([integrations, settings]) =>
						(integrations !== null && integrations !== undefined) ||
						(settings !== null && settings !== undefined),
				),
				takeUntil(this.destroy$),
			)
			.subscribe(([integrations, settings]) => {
				this.hasIntegration$.next(
					integrations && Object.values(integrations).some(x => x === true),
				);
				this.hasVerityIntegration$.next(integrations && integrations.verity === true);
				const formControls = this.getFormControls(settings);
				this.formHasRoomAndReservation$.next(
					formControls.some(c => c.field === "roomList") &&
						(formControls.some(c => c.field === "reservationId") ||
							formControls.some(c => c.field === "tripId")),
				);

				this.loginFormControls$.next(formControls.sort((a, b) => a.order - b.order));
				this.initLoginForm(
					this.loginFormControls$.value,
					this.formHasRoomAndReservation$.value,
				);
			});

		this.loginSubmit$
			.pipe(
				filter(val => val !== null && val !== undefined && val === true),
				withLatestFrom(this.organizationInfo$, this.guestValidationIntegrations$),
				takeUntil(this.destroy$),
			)
			.subscribe(([, organizationsInfo, integrations]) => {
				const payload: LoginPayloadParameters = this.getLoginFormPayload({
					businessId: organizationsInfo.business_id,
					firstName: this.loginForm.get("firstName")?.value,
					lastName: this.loginForm.get("lastName")?.value,
					reservationId: this.loginForm.get("reservationId")?.value,
					tripId: this.loginForm.get("tripId")?.value,
					email: this.loginForm.get("email")?.value,
					source: Object.keys(integrations).filter(key => integrations[key])[0] || null,
					room: this.loginForm.get("roomList")?.value,
					username: this.loginForm.get("username")?.value,
					password: this.loginForm.get("password")?.value,
				});

				this.authenticationFacade.login(payload);
			});

		this.loginUser$.pipe(ignoreNil(), takeUntil(this.destroy$)).subscribe(user => {
			if (user && Object.keys(user).length > 0) {
				// TODO: Check if we want to return customer data also
				// and where to use / what's the difference between customer id data
				const rememberMe = this.loginForm.get("rememberMe").value;
				this.authenticationFacade.setUserSession(user, rememberMe);

				if (this.returnUrl) {
					const urlArray = this.returnUrl.split("?");
					const queryParams = this.getUrlParams(urlArray.length > 1 ? urlArray[1] : "");
					this.navigationFacade.go({ path: [urlArray[0]], extras: { queryParams } });
				} else {
					this.navigationFacade.go({
						path: [`${this.commonFacade.loginUrl}/${user.uuid}/welcome`],
					});
				}
			} else {
				const data: TrpToastOptions = {
					message: "The credentials entered are not valid. Please try again",
					asNotification: true,
					type: TrpNotificationType.Warning,
					notificationPosition: TrpPosition.Right,
				};

				this.toastUtil.showToastAsNotification(data);
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.unsubscribe();
	}

	loginClicked(): void {
		if (!this.checkFormValidity()) {
			return;
		}

		this.loginSubmit$.next(true);
	}

	onTermsClick(): void {
		this.termsDialog.show();
	}

	onPrivacyClick(): void {
		this.privacyDialog.show();
	}

	private initLoginForm(
		formControls: { label: string; field: string; tooltip: string }[],
		formHasRoomAndReservation: boolean,
	): void {
		this.loginForm = new FormGroup({});

		formControls.forEach(control => {
			let validator = [];
			switch (control.field) {
				case "roomList":
				case "reservationId":
				case "tripId": {
					validator = formHasRoomAndReservation ? [] : [Validators.required];
					break;
				}
				case "firstName":
				case "lastName":
				case "email": {
					validator = [Validators.required];
					break;
				}
				default: {
					validator = [];
					break;
				}
			}
			this.loginForm.addControl(control.field, new FormControl(null, validator));
		});

		this.loginForm.addControl("rememberMe", new FormControl(false));
	}

	private getFormControls(
		settings: ValidationSettingsDTO,
	): { label: string; tooltip: string; field: string; placeholder: string; order: number }[] {
		return Object.keys(settings)
			.filter(key => settings[key])
			.map(field => {
				let order = 0;
				let label = "";
				let tooltip = "";
				let placeholder = "";
				switch (field) {
					default:
						break;
					case "firstName": {
						order = 1;
						label = "First name";
						placeholder = "Enter first name";
						break;
					}
					case "lastName": {
						order = 2;
						label = "Last name";
						placeholder = "Enter last name";
						break;
					}
					case "email": {
						order = 3;
						label = "Email";
						placeholder = "you@gmail.com";
						break;
					}
					case "reservationId": {
						order = 4;
						label = "Reservation ID";
						tooltip = this.RESERVATION_TOOLTIP_TXT;
						placeholder = "Enter reservation ID";
						break;
					}
					case "tripId": {
						order = 5;
						label = "Reservation ID";
						tooltip = this.RESERVATION_TOOLTIP_TXT;
						placeholder = "Enter reservation ID";
						break;
					}
					case "roomList": {
						order = 6;
						label = "Room Number";
						placeholder = "Example: 247";
						break;
					}
					case "username": {
						order = 1;
						label = "Username";
						placeholder = "Enter username";
						break;
					}
					case "password": {
						order = 2;
						label = "Password";
						placeholder = "Enter password";
						break;
					}
				}

				return {
					label,
					tooltip,
					field,
					placeholder,
					order,
				};
			});
	}

	private checkFormValidity(): boolean {
		if (!this.loginForm.valid) {
			this.loginForm.markAllAsTouched();
			this.loginForm.markAsDirty();
			Object.keys(this.loginForm.controls).forEach(key => {
				this.loginForm.controls[key].markAsDirty();
			});

			return false;
		}

		if (this.formHasRoomAndReservation$.value) {
			const reservationValue = this.loginForm.get("reservationId")?.value;
			const tripValue = this.loginForm.get("tripId")?.value;
			const roomValue = this.loginForm.get("roomList")?.value;

			if (!reservationValue && !tripValue && !roomValue) {
				const data: TrpToastOptions = {
					message:
						"Please enter either the reservation ID or room number to proceed with login",
					asNotification: true,
					type: TrpNotificationType.Warning,
					notificationPosition: TrpPosition.Right,
				};

				this.toastUtil.showToastAsNotification(data);

				return false;
			}
		}

		// We check username/password at form submission because we do not want
		// required state. If that is not the case add fields as required and
		// remove this code
		if (this.hasVerityIntegration$.value) {
			const usernameControl = this.loginForm.get("username") as FormControl;
			const passwordControl = this.loginForm.get("password") as FormControl;
			const usernameValue = usernameControl?.value;
			const passwordValue = passwordControl?.value;

			if (!usernameValue || !passwordValue) {
				if (!usernameValue) {
					usernameControl.markAsDirty();
					usernameControl.markAsTouched();
					usernameControl.setErrors({ required: true });
				}

				if (!passwordValue) {
					passwordControl.markAsDirty();
					passwordControl.markAsTouched();
					passwordControl.setErrors({ required: true });
				}

				return false;
			}
		}

		return true;
	}

	private getLoginFormPayload(config: {
		businessId: number;
		source: string;
		firstName?: string;
		lastName?: string;
		tripId?: string;
		reservationId?: string;
		email?: string;
		room?: string;
		username?: string;
		password?: string;
	}): LoginPayloadParameters {
		return {
			businessId: config.businessId,
			firstname: config.firstName,
			lastname: config.lastName,
			room: config.room,
			reservationId: config.reservationId,
			tripId: config.tripId,
			email: config.email,
			username: config.username,
			password: config.password,
			source: config.source,
		};
	}

	private getUrlParams(url: string): Record<string, string> {
		const urlParams = new URLSearchParams(url);

		return Object.fromEntries(urlParams.entries());
	}
}
